import { useEffect, useRef, useState } from "react";

import Backdrop from "@mui/material/Backdrop";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import CloseIcon from "@mui/icons-material/Close";
import FiberNewOutlinedIcon from "@mui/icons-material/FiberNewOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";

import { useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";
import { isNil } from "lodash";

import fpCommonService from "@plannel/services/fp/fp-common-service";
import fpSimulationScenarioService from "@plannel/services/fp/fp-simulation-scenario-service";
import PlanScope from "@plannel/pages/factory-plan/common/component/PlanScope";
import ScenarioPlannerPopup from "@plannel/pages/factory-plan/common/popup/ScenarioPlannerPopup";
import Dialog from "@plannel/components/Dialog";
import Snackbar from "@plannel/components/Snackbar";
import { SaveButton } from "@plannel/components/ActionIconButton";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";
import ColorUtil from "@plannel/pages/utils/color-util";

const { snackBarMessageColor } = ColorUtil;

// 규칙 3그룹 — 패널 순서/제목/저장 categoryCd가 전부 이 상수에서 나온다(old setDndPanel 3회 호출 대응).
// old는 categoryCd를 camel↔snake 변환으로 오갔지만, 그룹이 고정 3개라 상수 매핑이 더 명확하다.
const RULE_GROUPS = [
  { key: "planSeqRule", grpCd: "FP_PLAN_SEQ_RULE" },
  { key: "productionRule", grpCd: "FP_PRODUCTION_RULE" },
  { key: "stockUseRule", grpCd: "FP_STOCK_USE_RULE" }
];
const CATEGORY_PLAN_STRATEGY = "FP_PLAN_STRATEGY";
// 계획 수립 전략 라디오의 저장 값은 코드에서 FP_ 접두사를 뗀 값이다(old와 동일: FP_ORDER_SETTING → ORDER_SETTING).
const DEFAULT_PLAN_STRATEGY = "ORDER_SETTING";
// 계획 정책이 가리키는 기본 플래너 클래스. DB 기본값(tb_fp_plan_policy.planner_nm)과 같다.
const DEFAULT_PLANNER_NM = "DefaultObpPlanner";

const EMPTY_RULES = { planSeqRule: [], productionRule: [], stockUseRule: [] };

// 좌측 시나리오 목록 패널 폭 — SimulationStep(old ClickableListPanel 220px)과 동일.
const SCENARIO_LIST_WIDTH = 220;
const PANEL_HEADER_HEIGHT = 52;

const isBlank = value => isNil(value) || value === "";
const bySeq = (a, b) => Number(a.seq ?? 0) - Number(b.seq ?? 0);

const SimulationScenario = ({ t, viewName }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const [planScope, setPlanScope] = useState(reduxViewState?.planScope ?? "");

  // 좌측 시나리오(tb_fp_plan_policy) / 우측 상세(tb_fp_plan_policy_dtl)의 마스터-디테일 구성.
  const [scenarioList, setScenarioList] = useState([]);
  const [currentScenario, setCurrentScenario] = useState(null);
  // 아직 저장 전인 신규 시나리오 모드 — 저장 시 policyCd를 null로 보내면 서버가 채번한다.
  const [isNewScenario, setIsNewScenario] = useState(false);
  const [policyNm, setPolicyNm] = useState("");
  // 플래너 설정(플래너 클래스 + 인/아웃바운드 프로시저) — 팝업에서만 바꾼다.
  // plannerNm이 비어 있으면 저장 시 기본 플래너로 나간다.
  const [plannerSetting, setPlannerSetting] = useState(null);
  const [planStrategy, setPlanStrategy] = useState(DEFAULT_PLAN_STRATEGY);
  // 계획 수립 전략 라디오 옵션(FP_PLAN_DIRECTION 공통코드). 라벨은 렌더 시점에 t(comnCd)로 번역한다.
  const [directionOptions, setDirectionOptions] = useState([]);
  // 규칙 패널의 두 영역: 위 = 선택됨(배열 순서가 곧 itemSeq), 아래 = 미선택 풀(seq 정렬).
  const [selectedRules, setSelectedRules] = useState(EMPTY_RULES);
  const [availableRules, setAvailableRules] = useState(EMPTY_RULES);
  const [codesReady, setCodesReady] = useState(false);

  const [plannerPopupOpen, setPlannerPopupOpen] = useState(false);
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });
  const [progressOn, setProgressOn] = useState(false);

  const latestListReqRef = useRef(0);
  const latestDetailReqRef = useRef(0);
  // 규칙 3그룹의 전체 풀 — old 모듈 전역 ruleList 캐시의 마운트-로컬 판. 상세를 읽을 때마다
  // 이 원본에서 선택/미선택을 다시 가른다.
  const allRulesRef = useRef(EMPTY_RULES);

  const resetRulesToPool = () => {
    setSelectedRules(EMPTY_RULES);
    setAvailableRules({
      planSeqRule: [...allRulesRef.current.planSeqRule],
      productionRule: [...allRulesRef.current.productionRule],
      stockUseRule: [...allRulesRef.current.stockUseRule]
    });
  };

  // 상세 조회 — 선택 규칙은 itemSeq 순서로 복원한다(서버 쿼리에 ORDER BY가 없다).
  // 풀에 없는 저장값(폐기된 코드)은 old와 동일하게 화면에서 제외된다.
  const loadPolicyDetails = (policyCd) => {
    if (!planScope || isBlank(policyCd)) return;

    const reqId = ++latestDetailReqRef.current;

    fpCommonService.getPlanPolicyDetails({ viewId: viewName, subId: "", planScope, policyCd })
      .then(response => {
        if (reqId !== latestDetailReqRef.current) return;
        const details = response.data ?? [];

        const strategyRow = details.find(detail => detail.categoryCd === CATEGORY_PLAN_STRATEGY);
        setPlanStrategy(isBlank(strategyRow?.itemVal) ? DEFAULT_PLAN_STRATEGY : strategyRow.itemVal);

        const nextSelected = {};
        const nextAvailable = {};
        RULE_GROUPS.forEach(({ key, grpCd }) => {
          const pool = allRulesRef.current[key] ?? [];
          const selected = details
            .filter(detail => detail.categoryCd === grpCd)
            .sort((a, b) => Number(a.itemSeq ?? 0) - Number(b.itemSeq ?? 0))
            .map(detail => pool.find(rule => rule.comnCd === detail.itemVal))
            .filter(Boolean);
          const selectedCds = new Set(selected.map(rule => rule.comnCd));

          nextSelected[key] = selected;
          nextAvailable[key] = pool.filter(rule => !selectedCds.has(rule.comnCd));
        });
        setSelectedRules(nextSelected);
        setAvailableRules(nextAvailable);
      })
      .catch(e => {
        debug(e);
      });
  };

  // TODO(MIG-T2): [simulationscenario/SimulationScenario] 항목 전환/신규/취소 시 미저장 변경 경고(MSG_5142) 미이관.
  //   old: handleCancel이 신규 작성 중일 때 MSG_5142 확인을 받은 뒤에만 다른 시나리오로 전환했다.
  //   사유: FP 전 화면 공통(SimulationStep 등과 동일 계열) 유보 — 화면별로 다르게 가지 않는다.
  const selectScenario = (scenario) => {
    setIsNewScenario(false);
    setCurrentScenario(scenario ?? null);
    setPolicyNm(scenario?.policyNm ?? "");
    setPlannerSetting(scenario ? {
      plannerNm: scenario.plannerNm ?? null,
      inScriptNm: scenario.inScriptNm ?? "",
      inScriptUseYn: Boolean(scenario.inScriptUseYn),
      outScriptNm: scenario.outScriptNm ?? "",
      outScriptUseYn: Boolean(scenario.outScriptUseYn)
    } : null);

    if (scenario?.policyCd) {
      loadPolicyDetails(scenario.policyCd);
      return;
    }

    // 선택할 시나리오가 없으면(목록이 비었을 때) 진행 중인 상세 응답을 무효화하고 풀 전체로 되돌린다.
    // old는 이 경우 undefined.policyNm 접근으로 예외가 났다 — 빈 목록 가드는 new에서 보강한 것.
    latestDetailReqRef.current += 1;
    setPlanStrategy(DEFAULT_PLAN_STRATEGY);
    resetRulesToPool();
  };

  // 좌측 시나리오 목록 조회. 기본(defaultYn) 시나리오를 맨 앞으로 올리고(old와 동일),
  // 저장 직후에는 방금 저장한 시나리오(preferredPolicyCd)를 다시 선택한다.
  const retrieveScenarios = (preferredPolicyCd) => {
    if (!planScope) return;

    const reqId = ++latestListReqRef.current;

    fpCommonService.getPlanPolicies({ viewId: viewName, subId: "", planScope })
      .then(response => {
        if (reqId !== latestListReqRef.current) return;
        const results = [...(response.data ?? [])];

        const defaultIndex = results.findIndex(item => item.defaultYn === true);
        if (defaultIndex > 0) {
          results.unshift(results.splice(defaultIndex, 1)[0]);
        }
        setScenarioList(results);
        selectScenario(results.find(item => item.policyCd === preferredPolicyCd) ?? results[0] ?? null);
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        if (reqId !== latestListReqRef.current) return;
        const reduxState = reduxUtil.createObjectState(viewName, { planScope });
        reduxDispatch(reduxUtil.updateViewState(reduxState));
      });
  };

  // 신규 시나리오 — 좌측 선택을 풀고 이름/전략/규칙을 초기 상태로 되돌린다(old createScenario).
  const createScenario = () => {
    latestDetailReqRef.current += 1;

    setIsNewScenario(true);
    setCurrentScenario(null);
    setPolicyNm("");
    setPlannerSetting(null);
    setPlanStrategy(DEFAULT_PLAN_STRATEGY);
    resetRulesToPool();
  };

  // 풀 → 선택(맨 끝에 추가). 선택 배열의 순서가 저장 시 itemSeq가 된다.
  const addRule = (key, rule) => {
    setAvailableRules(prev => ({ ...prev, [key]: prev[key].filter(item => item.comnCd !== rule.comnCd) }));
    setSelectedRules(prev => ({ ...prev, [key]: [...prev[key], rule] }));
  };

  // 선택 → 풀(코드 seq 순서 자리로 복귀 — old도 되돌린 항목을 풀 순서에 끼워 넣지는 못했지만,
  // 풀은 순서가 데이터가 아니므로 정렬 복귀가 자연스럽다).
  const removeRule = (key, rule) => {
    setSelectedRules(prev => ({ ...prev, [key]: prev[key].filter(item => item.comnCd !== rule.comnCd) }));
    setAvailableRules(prev => ({ ...prev, [key]: [...prev[key], rule].sort(bySeq) }));
  };

  // 선택 목록 안에서 이웃과 자리 바꿈(↑/↓).
  const moveRule = (key, index, offset) => {
    setSelectedRules(prev => {
      const list = [...prev[key]];
      const targetIndex = index + offset;
      if (targetIndex < 0 || targetIndex >= list.length) return prev;
      [list[index], list[targetIndex]] = [list[targetIndex], list[index]];
      return { ...prev, [key]: list };
    });
  };

  const saveScenario = () => {
    if (isBlank(policyNm)) {
      setProgressOn(false);
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_PleaseEnterValues", { values: t("FP_POLICY_NM") }) });
      return;
    }

    const policyCd = isNewScenario ? null : (currentScenario?.policyCd ?? null);

    // 선택 목록 순서 그대로 itemSeq를 매긴다 + 계획 수립 전략 1행(old saveData와 동일 payload).
    // "변경 없음" 판정은 두지 않는다 — old도 무조건 저장이고, 서버가 상세를 전량 교체(delete-all +
    // reinsert)하므로 재저장이 안전하다.
    const details = RULE_GROUPS.flatMap(({ key, grpCd }) =>
      selectedRules[key].map((rule, index) => ({ policyCd, categoryCd: grpCd, itemVal: rule.comnCd, itemSeq: index })));
    details.push({ policyCd, categoryCd: CATEGORY_PLAN_STRATEGY, itemVal: planStrategy, itemSeq: 0 });

    fpSimulationScenarioService.save({
      planScope,
      policyCd,
      policyNm,
      plannerNm: plannerSetting?.plannerNm || DEFAULT_PLANNER_NM,
      inScriptNm: plannerSetting?.inScriptNm ?? "",
      inScriptUseYn: Boolean(plannerSetting?.inScriptUseYn),
      outScriptNm: plannerSetting?.outScriptNm ?? "",
      outScriptUseYn: Boolean(plannerSetting?.outScriptUseYn),
      details
    })
      .then(response => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
        // 신규 시나리오는 서버가 policyCd(FP_POLICY_<yyyyMMddHHmmss>)를 채번해 newPlanPolicy로
        // 돌려준다 — 목록을 다시 읽고 그 시나리오를 선택한다.
        retrieveScenarios(response.data?.data?.newPlanPolicy?.policyCd ?? currentScenario?.policyCd);
      })
      .catch(e => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
        debug(e);
      })
      .then(() => {
        setProgressOn(false);
      });
  };

  const handleDialog = (action) => {
    if (action === "SAVE") {
      setProgressOn(true);
      saveScenario();
    }
  };

  // 공통코드 로드(전략 방향 + 규칙 3그룹) — planScope와 무관해서 마운트에 1회만.
  useEffect(() => {
    const loadCodes = (grpCd) => fpCommonService.getCodes(grpCd).then(response => [...(response.data ?? [])].sort(bySeq));

    Promise.all([loadCodes("FP_PLAN_DIRECTION"), ...RULE_GROUPS.map(({ grpCd }) => loadCodes(grpCd))])
      .then(([directions, planSeqRule, productionRule, stockUseRule]) => {
        setDirectionOptions(directions.map(code => ({ value: code.comnCd.replace(/^FP_/, ""), comnCd: code.comnCd })));
        allRulesRef.current = { planSeqRule, productionRule, stockUseRule };
      })
      .catch(e => {
        debug(e);
      })
      // 코드 로드가 실패해도 목록 조회는 진행한다(빈 풀/빈 라디오로 표시) — old getSettingData가
      // 실패를 삼키고 getScenarioList로 넘어가던 것과 동일한 경로(감사 N-3 반영).
      .then(() => {
        setCodesReady(true);
      });
  }, []);

  useEffect(() => {
    if (!codesReady || !planScope) return;
    retrieveScenarios();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codesReady, planScope]);

  const ruleLabel = (rule) => (rule.attr01Val === "Y" ? t(rule.comnCdNm) : t(rule.comnCd));

  // TODO(MIG-T2): [simulationscenario/SimulationScenario] 규칙 드래그앤드롭 인터랙션 미이관.
  //   old: @hello-pangea/dnd 이중 드롭존(풀→선택 끌어올리기, 선택 내 드래그 순서변경) + 안내 문구
  //        FP_MSG_DRAG_RULE/FP_MSG_ADJUST_DRAG_DROP(드래그 전제 문구라 키도 함께 유보, 2차에서 추가).
  //   사유: new에 DnD 라이브러리가 없다(new-equivalents-report ❌, 의존성 추가 금지). 선택/해제/순서
  //        변경과 저장 payload(itemSeq)는 클릭+↑↓ 버튼으로 동일하게 동작한다(2026-08-09 사용자 확정).
  const renderRulePanel = ({ key, grpCd }) => (
    <Box key={key} sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", border: 1, borderColor: "divider", borderRadius: 1, overflow: "hidden" }}>
      <Box sx={{ px: 1.5, py: 1, flex: "0 0 auto", borderBottom: 1, borderColor: "divider" }}>
        <Typography variant="subtitle2">{t(grpCd)}</Typography>
      </Box>
      {/* 위 = 선택된 규칙(순서가 곧 저장되는 itemSeq) */}
      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", p: 1 }}>
        {selectedRules[key].map((rule, index) => (
          <Box key={rule.comnCd} sx={{ display: "flex", alignItems: "center", gap: 0.5, pl: 1, pr: 0.5, py: 0.25, mb: 0.5, bgcolor: "action.selected", borderRadius: 1 }}>
            <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>{ruleLabel(rule)}</Typography>
            <Tooltip title={t("FP_CHANGE_SEQ")}>
              <span>
                <IconButton aria-label="moveRuleUp" size="small" disabled={index === 0} onClick={() => moveRule(key, index, -1)}>
                  <ArrowUpwardIcon fontSize="inherit" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={t("FP_CHANGE_SEQ")}>
              <span>
                <IconButton aria-label="moveRuleDown" size="small" disabled={index === selectedRules[key].length - 1} onClick={() => moveRule(key, index, 1)}>
                  <ArrowDownwardIcon fontSize="inherit" />
                </IconButton>
              </span>
            </Tooltip>
            <IconButton aria-label="removeRule" size="small" onClick={() => removeRule(key, rule)}>
              <CloseIcon fontSize="inherit" />
            </IconButton>
          </Box>
        ))}
      </Box>
      {/* 아래 = 미선택 풀(클릭하면 위 목록 끝에 추가) */}
      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", p: 1, borderTop: 1, borderColor: "divider" }}>
        {availableRules[key].map(rule => (
          <Box
            key={rule.comnCd}
            onClick={() => addRule(key, rule)}
            sx={{ display: "flex", alignItems: "center", gap: 0.5, px: 1, py: 0.5, mb: 0.5, borderRadius: 1, cursor: "pointer", "&:hover": { bgcolor: "action.hover" } }}
          >
            <AddIcon fontSize="small" color="action" />
            <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>{ruleLabel(rule)}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );

  return (
    <>
      {/* TODO(MIG-T3): [simulationscenario/SimulationScenario] 조회영역 없음 표시(NO_SEARCHAREA) 미이관.
          old: useEffect에서 setViewInfo(activeViewId, 'NO_SEARCHAREA', true)로 전역 뷰 정보에 등록.
          사유: new에는 화면별 뷰 정보 레지스트리라는 개념 자체가 없다(Tier3). */}
      <FilterContainer hasIcons={false}>
        <PlanScope value={planScope} onChange={setPlanScope} />
      </FilterContainer>
      <Box sx={{ display: "flex", gap: 1.5, flex: 1, minHeight: 0, mt: 1, mb: 1 }}>
        <Box sx={{ width: SCENARIO_LIST_WIDTH, flex: "0 0 auto", display: "flex", flexDirection: "column", border: 1, borderColor: "divider", borderRadius: 1, overflow: "hidden" }}>
          <Box sx={{ height: PANEL_HEADER_HEIGHT, flex: "0 0 auto", display: "flex", alignItems: "center", px: 1.5, borderBottom: 1, borderColor: "divider" }}>
            <Button fullWidth size="small" variant="outlined" startIcon={<AddIcon />} onClick={createScenario} sx={{ minHeight: 32, lineHeight: 1.2 }}>
              {t("FP_SCENARIO_CREATION")}
            </Button>
          </Box>
          <List sx={{ py: 2, flex: 1, minHeight: 0, overflow: "auto" }}>
            {scenarioList.map(scenario => (
              <ListItem key={scenario.policyCd} disablePadding>
                <ListItemButton
                  selected={!isNewScenario && currentScenario?.policyCd === scenario.policyCd}
                  onClick={() => selectScenario(scenario)}
                  sx={{
                    minHeight: 32,
                    px: "14px",
                    py: 1,
                    borderLeft: 3,
                    borderColor: !isNewScenario && currentScenario?.policyCd === scenario.policyCd ? "primary.main" : "transparent"
                  }}
                >
                  <ListItemText primary={scenario.policyNm} primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", overflow: "hidden", border: isNewScenario ? 2 : 1, borderColor: isNewScenario ? "primary.main" : "divider", borderRadius: 1 }}>
          <Box sx={{ height: PANEL_HEADER_HEIGHT, flex: "0 0 auto", display: "flex", alignItems: "center", gap: 1, px: 1.5, borderBottom: 1, borderColor: "divider" }}>
            <TextField
              size="small"
              label={t("FP_POLICY_NM")}
              placeholder={t("FP_MSG_ENTER_DESCRIP")}
              value={policyNm}
              onChange={event => setPolicyNm(event.target.value)}
              sx={{ width: 600, maxWidth: "55%" }}
            />
            <Tooltip title={t("FP_SELECT_SCENARIO_PLANNER")}>
              <IconButton aria-label="selectScenarioPlanner" color="info" size="small" onClick={() => setPlannerPopupOpen(true)}>
                <InsertDriveFileOutlinedIcon />
              </IconButton>
            </Tooltip>
            {isNewScenario && <FiberNewOutlinedIcon color="primary" />}
            <Box sx={{ ml: "auto", display: "flex", alignItems: "center" }}>
              {isNewScenario && (
                <Button size="small" onClick={() => selectScenario(scenarioList[0] ?? null)}>
                  {t("cancel")}
                </Button>
              )}
              <SaveButton onClick={() => setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") })} />
            </Box>
          </Box>
          <Box sx={{ p: 1.5, flex: 1, minHeight: 0, display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Box sx={{ flex: "0 0 auto", border: 1, borderColor: "divider", borderRadius: 1, px: 1.5, py: 1 }}>
              <Typography variant="subtitle2" gutterBottom>{t("FP_PLAN_STRATEGY")}</Typography>
              <RadioGroup row value={planStrategy} onChange={event => setPlanStrategy(event.target.value)}>
                {directionOptions.map(option => (
                  <FormControlLabel key={option.value} value={option.value} control={<Radio size="small" />} label={t(option.comnCd)} />
                ))}
              </RadioGroup>
            </Box>
            <Box sx={{ display: "flex", gap: 1.5, flex: 1, minHeight: 0 }}>
              {RULE_GROUPS.map(renderRulePanel)}
            </Box>
          </Box>
        </Box>
      </Box>
      <ScenarioPlannerPopup
        open={plannerPopupOpen}
        value={plannerSetting}
        onClose={() => setPlannerPopupOpen(false)}
        onConfirm={value => {
          setPlannerSetting(value);
          setPlannerPopupOpen(false);
        }}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      <Backdrop sx={{ color: "#fff", zIndex: 10000 }} open={progressOn}>
        <CircularProgress color="inherit" size={"80px"} />
      </Backdrop>
    </>
  );
};

export default withTranslation()(SimulationScenario);
